import React, { useState, useEffect, useMemo } from 'react';
import DataTable from 'react-data-table-component';
import { userRequest } from '../utils/requestMethods';
import { useStateContext } from '../contexts/ContextProvider';
import { CustomPagination, SkeletonTable } from './index';
import { MdEmail, MdPerson, MdPhone } from 'react-icons/md';
import { FiSearch, FiDownload } from 'react-icons/fi';
import { exportToExcel } from '../utils/exportToExcel';

const customStyles = {
  headRow: {
    style: {
      backgroundColor: 'rgb(248, 250, 252)',
      fontWeight: "bold",
    },
  },
};

const ParentsTable = () => {
  const { school } = useStateContext();
  const [loading, setLoading] = useState(true);
  const [parents, setParents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [classes, setClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState('all');

  const fetchParents = async (classId = 'all') => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '1000' });
      if (classId && classId !== 'all') {
        params.append('class_id', classId);
      }
      const endpoint = `/schools/${school?._id}/parents?${params.toString()}`;
      const { data } = await userRequest.get(endpoint);
      // Handle the response data structure
      if (data.parents && Array.isArray(data.parents)) {
        // If API returns structured response with parents array
        setParents(data.parents);
      } else if (Array.isArray(data)) {
        // If API returns array directly
        setParents(data);
      } else {
        // Handle other response structures
        setParents([]);
      }
    } catch (error) {
      console.error('Error fetching parents:', error);
      setParents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (school?._id) {
      fetchParents(selectedClassId);
    }
  }, [school?._id, selectedClassId]);

  const fetchClasses = async () => {
    if (!school?.code && !school?._id) return;
    try {
      setClassesLoading(true);
      let endpoint = '';
      if (school?.code) {
        endpoint = `/classes?code=${school.code}`;
      } else {
        endpoint = `/classes/school/${school._id}`;
      }
      const { data } = await userRequest.get(endpoint);
      const classesData = Array.isArray(data?.classes) ? data.classes : Array.isArray(data) ? data : [];
      setClasses(classesData);
    } catch (error) {
      console.error('Error fetching classes:', error);
      setClasses([]);
    } finally {
      setClassesLoading(false);
    }
  };

  useEffect(() => {
    if (school?.code || school?._id) {
      fetchClasses();
    }
  }, [school?.code, school?._id]);

  const filteredParents = useMemo(() => {
    if (!searchTerm.trim()) return parents;

    const term = searchTerm.toLowerCase();
    return parents.filter((parent) => {
      const name = `${parent.firstname || ''} ${parent.lastname || ''}`.toLowerCase();
      const email = (parent.email || '').toLowerCase();
      const phone = (parent.mobile_number || '').toLowerCase();
      return (
        name.includes(term) ||
        email.includes(term) ||
        phone.includes(term)
      );
    });
  }, [parents, searchTerm]);

  const handleClassChange = (event) => {
    const newClassId = event.target.value;
    setSelectedClassId(newClassId);
    setSearchTerm('');
  };

  const columns = [
    {
      name: "Full Name",
      selector: row => (
        <div className="flex items-center">
          <MdPerson className="text-gray-500 mr-2" />
          <span className="font-semibold">{`${row.firstname} ${row.lastname}`}</span>
        </div>
      ),
      sortable: true,
    },
    {
      name: "Email",
      selector: row => (
        <div className="flex items-center">
          <MdEmail className="text-gray-500 mr-2" />
          <span className="font-semibold">{row.email || 'N/A'}</span>
        </div>
      ),
      sortable: true,
    },
    {
      name: "Phone Number",
      selector: row => (
        <div className="flex items-center">
          <MdPhone className="text-gray-500 mr-2" />
          <span className="font-semibold">{row.mobile_number || 'N/A'}</span>
        </div>
      ),
      sortable: true,
    }
  ];

  // Separate from `columns` above — those selectors return icon-wrapped JSX,
  // which would export as garbled JSON when passed straight into the sheet.
  const exportColumns = [
    { name: 'Full Name', selector: row => `${row.firstname || ''} ${row.lastname || ''}`.trim() },
    { name: 'Email', selector: row => row.email || '' },
    { name: 'Phone Number', selector: row => row.mobile_number || '' },
  ];

  const handleExport = () => {
    const selectedClass = classes.find(cls => cls._id === selectedClassId);
    const classLabel = selectedClass ? selectedClass.class_name.replace(/\s+/g, '_') : 'All_Classes';
    const date = new Date().toISOString().split('T')[0];
    exportToExcel(filteredParents, exportColumns, `Parents_${classLabel}_${date}.xlsx`, 'Parents');
  };

  return (
    <div className="bg-white rounded-3xl shadow-sm border border-gray-200">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-4">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-600">Filter by Class:</span>
          <select
            value={selectedClassId}
            onChange={handleClassChange}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#3F3DA5] focus:border-transparent"
          >
            <option value="all">All Classes</option>
            {classes.map((cls) => (
              <option key={cls._id} value={cls._id}>
                {cls.class_name}
              </option>
            ))}
          </select>
          {classesLoading && (
            <span className="inline-flex items-center text-xs text-gray-500">
              <span className="w-4 h-4 mr-2 border-2 border-gray-300 border-t-[#3F3DA5] rounded-full animate-spin"></span>
              Loading classes...
            </span>
          )}
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative w-full sm:w-64">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, or phone"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3F3DA5] focus:border-transparent text-sm"
            />
          </div>
          <button
            type="button"
            onClick={handleExport}
            disabled={filteredParents.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full text-white bg-[#3F3DA5] hover:bg-[#2E2D7A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
          >
            <FiDownload className="text-sm" />
            Export to Excel
          </button>
        </div>
      </div>

      <div className="px-4">
      <DataTable
        data={filteredParents}
        columns={columns}
        getRowId={(row) => row._id}
        pagination
        paginationComponent={CustomPagination}
        paginationRowsPerPageOptions={[10, 20, 30, 50]}
        customStyles={customStyles}
        progressPending={loading}
        progressComponent={<SkeletonTable rows={5} columns={3} />}
        highlightOnHover
        striped
      />
      </div>
    </div>
  );
};

export default ParentsTable; 